const FieldValidators = {
  // Applies to both Specials & Volume Buys
  alertOnEmptyRowFields: (alertableField, fieldsToValidate = []) => {
    const hasFieldWithValue = fieldsToValidate.find((field) => field > 0)
    return !(hasFieldWithValue && !alertableField) || 'Value required'
  },
  // Applies to both Single & Multi Specials
  rulesSpecialAmountOff: (amountOff, cartonPrice) => {
    let result
    if (!(!amountOff || amountOff >= 0)) result = 'Amount Off must be positive'
    else if (!(!amountOff || amountOff > 0)) result = 'Amount Off required'
    else if (!(amountOff < cartonPrice))
      result = 'Amount Off must be lower than Carton Price'
    else result = true
    return result
  },
  // Applies to both Single & Multi Specials
  rulesSpecialUnitRrp: (
    unitRrp,
    grossProfitPercentage,
    variableWeightProductSelected
  ) => {
    let result
    if (!(!unitRrp || unitRrp >= 0)) {
      result = variableWeightProductSelected
        ? 'RRP / KG must be positive'
        : 'Unit RRP must be positive'
    } else if (!(!unitRrp || unitRrp > 0)) {
      result = variableWeightProductSelected
        ? 'RRP / KG required'
        : 'Unit RRP required'
    } else if (!(!grossProfitPercentage || grossProfitPercentage > 0)) {
      result = 'GP% cannot be negative'
    } else result = true
    return result
  },
  // Applies to both Single & Multi Volume Buys
  rulesVolumeTierNumberOfCartons: (numberOfCartons) => {
    let result
    if (!(!numberOfCartons || numberOfCartons >= 0))
      result = 'Number of Cartons must be positive'
    else if (!(!numberOfCartons || numberOfCartons <= 9999))
      result = 'Number of Cartons must not exceed 9999'
    else if (!(!numberOfCartons || numberOfCartons > 0))
      result = 'Number of Cartons required'
    else result = true
    return result
  },

  rulesVolumeTierAmountOff: (
    amountOff,
    cartonPrice,
    tiers = [],
    numberOfCartons
  ) => {
    let result
    const value = Number(amountOff)

    if (!(!amountOff || amountOff >= 0)) {
      result = 'Amount Off Carton must be positive'
    } else if (!(!amountOff || amountOff > 0)) {
      result = 'Amount Off Carton required'
    } else if (!(!amountOff || amountOff < cartonPrice)) {
      result = 'Amount Off Carton must be lower than Carton Price'
    } else {
      if (Array.isArray(tiers) && tiers.length && numberOfCartons) {
        const cartons = Number(numberOfCartons)
        const sortedTiers = [...tiers].sort(
          (a, b) => a.numberOfCartons - b.numberOfCartons
        )
        const lowerTier = sortedTiers
          .filter((tier) => tier.numberOfCartons < cartons)
          .pop()
        if (lowerTier && value <= Number(lowerTier.amountOff)) {
          result = `Amount Off Carton must be greater than the previous one.`
        } else {
          result = true
        }
      } else {
        result = true
      }
    }

    return result
  },

  // Applies to both Single & Multi Volume Buys
  rulesVolumeTierUnitRrp: (
    unitRrp,
    grossProfitPercentage,
    variableWeightProductSelected
  ) => {
    let result
    if (!(!unitRrp || unitRrp >= 0)) {
      result = variableWeightProductSelected
        ? 'RRP / KG must be positive'
        : 'Unit RRP must be positive'
    } else if (!(!unitRrp || unitRrp > 0)) {
      result = variableWeightProductSelected
        ? 'RRP / KG required'
        : 'Unit RRP required'
    } else if (!(!grossProfitPercentage || grossProfitPercentage > 0)) {
      result = 'GP% cannot be negative'
    } else result = true
    return result
  },
  // All below apply to Sign up or User details processes
  rulesBusinessABN: [
    (v) => !!v || 'ABN required',
    (v) => /^[0-9 ]+$/.test(v) || 'Only numbers and spaces allowed',
    (v) => v?.length >= 11 || '11 characters min. (numbers and spaces only)',
    (v) => v?.length <= 14 || '14 characters max (numbers and spaces only)'
  ],
  rulesCurrentPasswordRequired: [(v) => !!v || 'Current password is required'],
  rulesEmail: [
    (v) => !!v || 'Email Address required',
    (v) => /.+@.+/.test(v) || 'Email must be valid'
  ],
  rulesFirstName: [
    (v) => !!v || 'First Name required',
    // Note currently the error message below will never get displayed as we allow all characters.  If we ever decide
    // to change this the error message will need to be updated
    (v) => /^.*$/.test(v) || 'All characters are allowed'
  ],
  rulesLastName: [
    (v) => !!v || 'Last Name required',
    // Note currently the error message below will never get displayed as we allow all characters.  If we ever decide
    // to change this the error message will need to be updated
    (v) => /^.*$/.test(v) || 'All characters are allowed'
  ],
  rulesPhone: [
    (v) => !!v || 'Phone Number required',
    (v) =>
      /^[0-9()+-]+$/.test(v) || 'Numbers & special characters only (no spaces)'
  ],
  rulesPostCode: [
    (v) => !!v || 'Postcode required',
    (v) => /^[0-9]+$/.test(v) || 'Numbers only',
    (v) => v?.length === 4 || 'Must be 4 digits'
  ],
  // All below apply to Product creation / edition
  fieldRequired: (value, message = 'Field required') => {
    if (value) {
      if (typeof value === 'string') {
        return value.trim().length > 0 || message
      } else {
        return true
      }
    }
    return message
  },
  gtinLengthRule: (value, allowedLengths = [0, 8, 12, 13, 14]) => {
    const gtin = value ?? ''
    return (
      allowedLengths.includes(gtin.length) ||
      'GTIN format must be 8, 12, 13 or 14 characters'
    )
  },
  higherThanZero: (value) => {
    return value > 0 || 'Cannot be 0'
  },
  maxCartonWeight: (value, maxCartonWeight = 9999.99) => {
    return (
      (value && value <= maxCartonWeight) ||
      `Carton Weight cannot be over ${maxCartonWeight}`
    )
  },
  maxKeywordsRule: (value, maxKeywords = 5) => {
    return (
      (value && value.length <= maxKeywords) ||
      `Max. ${maxKeywords} keywords allowed`
    )
  },
  maxUnitSize: (value, maxUnitSize = 9999.99) => {
    return (
      (value && value <= maxUnitSize) ||
      `Unit Size cannot be over ${maxUnitSize}`
    )
  },
  numbersOnlyRule: (value) => {
    return !value || /^[0-9]+$/.test(value) || 'Numbers only'
  },
  positiveNumberRule: (value) => {
    const sanitizedValue = value?.toString()?.replaceAll(/[$,]/g, '')
    return (
      Number(sanitizedValue) >= 0 ||
      typeof value === 'undefined' ||
      'Number must be positive'
    )
  },
  textAreaLengthRule: (value, maxCharacters = 1000) => {
    return (
      !value ||
      value?.length <= maxCharacters ||
      `Max. ${maxCharacters} characters`
    )
  },
  // Related to features with starting and ending date
  validStartSate: (startDate, endDate) => {
    if (endDate)
      return endDate >= startDate || 'Start Date must be on or before End Date'
    else return true
  },
  validEndDate: (startDate, endDate) => {
    return endDate >= startDate || 'End Date must be on or after Start Date'
  },
  conditionally_require_value: (v, condition) => {
    if (!condition) return true
    return v === 0 || !!v || 'Value required'
  },
  require_value: (v) => {
    return v === 0 || !!v || 'Value required'
  }
}

export default FieldValidators
